import { showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { nanoid } from 'nanoid'
import { useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'

import changeSet from 'core/change_set'
import { useEventTypeContext } from 'features/event_type_provider'

import { BookingPreviewSync } from './booking_preview_sync'
import { DirtyTracking } from './dirty_tracking'
import { Footer } from './footer'
import { FormBody, StyledForm, Wrapper } from './styles'
import { useFormInitialValidation } from './utils'
import { ValidationErrorAlert } from './validation_error_alert'
import { useAccordionState } from '../components/accordion'

export const BaseSection = ({
  children,
  enableReinitialize = true,
  formatValues = (v, _panelKeys, _initialValues) => v,
  initialValues = {},
  panelFieldsMapping,
  validationSchema,
  valuesMapper = (v) => v,
  externalError,
  onSubmit,
  selectedSectionRef,
  ...rest
}) => {
  const dispatch = useDispatch()
  const formInnerRef = useRef()
  const formId = useMemo(() => nanoid(), [])
  const { isCreation } = useEventTypeContext()
  const { selectedKeys } = useAccordionState()

  const handleSubmit = useCallback((values) => {
    const isFormDirty = changeSet.isDirty(formId)
    const hasUserToggledNotetaker = Boolean(values?.hasUserToggledNotetaker)
    const shouldSubmitForNotetakerOnlyChange = !isCreation
      && !isFormDirty
      && typeof values?.notetakerEnabledForEt === 'boolean'
      && hasUserToggledNotetaker

    if (!isFormDirty && !isCreation && !shouldSubmitForNotetakerOnlyChange) {
      dispatch(showSuccess(I18n.t('publisher.event_type_editor.saved')))
      return Promise.resolve()
    }

    // The tweak using selectedKeys is necessary to update memberships for the open panel only.
    // The tweak using initialValues is necessary to update only changed memberships.
    return Promise.resolve(onSubmit(formatValues(values, selectedKeys, initialValues), values)).then((result) => {
      // Prevent repeated "notetaker-only" submits after a successful save.
      formInnerRef.current?.setFieldTouched?.('notetakerEnabledForEt', false, false)
      formInnerRef.current?.setFieldValue?.('hasUserToggledNotetaker', false, false)
      return result
    })
  }, [dispatch, formId, formatValues, initialValues, isCreation, onSubmit, selectedKeys])

  useFormInitialValidation({ isCreation, panelFieldsMapping, formInnerRef })

  return (
    <Wrapper {...rest}>
      <StyledForm
        innerRef={formInnerRef}
        enableReinitialize={enableReinitialize}
        externalError={externalError}
        initialValues={initialValues}
        validateOnChange
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <BookingPreviewSync />
        <DirtyTracking formId={formId} valuesMapper={valuesMapper} />
        <ValidationErrorAlert />
        <FormBody spaceless>
          {children}
        </FormBody>
        <Footer selectedSectionRef={selectedSectionRef} />
      </StyledForm>
    </Wrapper>
  )
}
